/**
 * A time series drawn in the order time runs (CT-5725).
 *
 * The rev4 engine groups by a date and hands the buckets back **unsorted** in
 * `series[].points`, while sorting the same buckets in the sibling arrays:
 * measured on dev 2026-10-26, one `STACKED_BAR` response carried
 * `totals` as `10-13 … 10-26` and its own `series[0].points` as
 * `10-22, 10-16, 10-26, 10-14, …`, and `HEATMAP` sorted its `x_categories`
 * while `LINE_CHART` and `AREA_CHART` did not. A line drawn in that order is
 * a zig-zag that reads as noise, so the order is restored here.
 *
 * **Only when every `x` is a date.** A categorical axis carries an order of
 * its own — funnel stages, the `__others__` bucket the engine appends last —
 * and sorting `AGT500` next to `DSW029` would be rearranging someone else's
 * answer. The engine's date buckets are ISO and fixed-width
 * (`2026-10-13`, `2026-10`, `2026-10-13T22:09:24Z`), so they sort as strings:
 * no `Date` parsing, and no timezone to get wrong.
 *
 * The data is returned untouched when it is already in order, so a redraw
 * keeps the same object.
 */

/** An engine date bucket: `YYYY-MM`, `YYYY-MM-DD`, or either with a time. */
const TEMPORAL_X = /^\d{4}-\d{2}(-\d{2})?([T ].*)?$/;

const isTemporal = (point) => typeof point?.x === 'string' && TEMPORAL_X.test(point.x);

const byX = (a, b) => {
  if (a.x === b.x) return 0;
  return a.x < b.x ? -1 : 1;
};

const isSorted = (points) => points.every((point, i) => i === 0 || byX(points[i - 1], point) <= 0);

const sorted = (points) => (isSorted(points) ? points : [...points].sort(byX));

/**
 * @param {object|null} data The shaped `data` of an execution result.
 * @returns {object|null} The same data with every temporal series in date
 *   order — the same object when there was nothing to reorder.
 */
export const withOrderedSeries = (data) => {
  if (!data || !Array.isArray(data.series)) return data;

  /*
    One decision for the whole widget: a stacked bar's series share an axis,
    so ordering one and not another would tear the stack apart. `totals`
    rides along for the same reason — it is drawn against the same axis.
  */
  const lists = [...data.series.map((series) => series.points), data.totals]
    .filter(Array.isArray);
  if (!lists.length) return data;

  const points = lists.flat();
  if (points.length < 2 || !points.every(isTemporal)) return data;
  if (lists.every(isSorted)) return data;

  return {
    ...data,
    series: data.series.map((series) => (Array.isArray(series.points)
      ? { ...series, points: sorted(series.points) }
      : series)),
    ...(Array.isArray(data.totals) ? { totals: sorted(data.totals) } : {}),
  };
};

export default withOrderedSeries;
